import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { initializeFirebase, CloudFunctions } from "@services/firebase";
import * as AuthService from "@services/firebase/AuthService.js";
import { useAdminStore } from "./useAdminStore.js";

// Helper function to get management tokens from localStorage
function getManagementTokensFromStorage() {
  try {
    const tokens = localStorage.getItem("wheelManagementTokens");
    const parsed = tokens ? JSON.parse(tokens) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error("Error reading management tokens from localStorage:", error);
    return [];
  }
}

export const useUserStore = defineStore("user", () => {
  // State
  const userPhotoUrl = ref("");
  const displayName = ref("");
  const savedWheels = ref([]);
  const sharedWheels = ref([]);
  const managementTokens = ref([]);
  const initialized = ref(false);
  const isAuthenticated = ref(false);
  const userProfile = ref(null);
  const isExistingUser = ref(false);

  // Getters
  const isInitialized = computed(() => initialized.value);

  const getUserPhotoUrl = computed(() => {
    // Priority: backend profile -> committed state -> auth fallback -> default
    const authUser =
      (AuthService.getCurrentUser && AuthService.getCurrentUser()) || null;

    const profileUrl =
      userProfile.value && userProfile.value.photoURL
        ? userProfile.value.photoURL
        : "";
    if (profileUrl) return profileUrl;

    const stateUrl = userPhotoUrl.value ? userPhotoUrl.value : "";
    if (stateUrl) return stateUrl;

    const authUrl = authUser && authUser.photoURL ? authUser.photoURL : "";
    if (authUrl) return authUrl;

    return "/images/user_profile.png";
  });

  const getDisplayName = computed(() => {
    // Priority: backend profile -> committed state -> auth fallback (displayName/email) -> empty
    const authUser =
      (AuthService.getCurrentUser && AuthService.getCurrentUser()) || null;

    const profileName =
      userProfile.value &&
      userProfile.value.displayName &&
      userProfile.value.displayName.trim()
        ? userProfile.value.displayName.trim()
        : "";
    if (profileName) return profileName;

    const stateName =
      displayName.value && displayName.value.trim()
        ? displayName.value.trim()
        : "";
    if (stateName) return stateName;

    const email =
      (userProfile.value && userProfile.value.email) ||
      (authUser ? authUser.email || "" : "");
    if (email) return email;

    const authDisplay =
      authUser && authUser.displayName ? authUser.displayName : "";
    return authDisplay || "";
  });

  const userEmail = computed(() => {
    const profileEmail =
      userProfile.value && userProfile.value.email
        ? userProfile.value.email
        : "";
    if (profileEmail) return profileEmail;

    const authUser =
      (AuthService.getCurrentUser && AuthService.getCurrentUser()) || null;
    return authUser && authUser.email ? authUser.email : "";
  });

  // Auth getters that delegate to AuthService
  // Note: These are functions returning boolean/objects, but usually getters are properties.
  // In Vuex they were properties. In Pinia computed properties work same way.
  // However AuthService methods are not reactive by themselves usually unless they tap into some reactive streams.
  // isAuthenticated state handles the reactivity for "is logged in".

  const isAuthReady = computed(() => AuthService.isAuthenticationReady());
  const isUserLoggedIn = computed(() => !!isAuthenticated.value);
  const hasGoogleLinked = computed(() => AuthService.userHasGoogleProvider());
  const currentUser = computed(() => AuthService.getCurrentUser());

  const isEmailVerified = computed(() => {
    if (
      userProfile.value &&
      typeof userProfile.value.emailVerified === "boolean"
    ) {
      return !!userProfile.value.emailVerified;
    }
    const authUser =
      (AuthService.getCurrentUser && AuthService.getCurrentUser()) || null;
    return !!(authUser && authUser.emailVerified);
  });

  const providers = computed(() => {
    const profileProviders = Array.isArray(
      userProfile.value && userProfile.value.providers
    )
      ? userProfile.value.providers
      : null;
    if (profileProviders) return profileProviders;

    const authUser =
      (AuthService.getCurrentUser && AuthService.getCurrentUser()) || null;
    if (authUser && Array.isArray(authUser.providerData)) {
      try {
        return authUser.providerData.map((p) => p.providerId);
      } catch {
        return [];
      }
    }
    return [];
  });

  const hasPasswordProvider = computed(
    () => Array.isArray(providers.value) && providers.value.includes("password")
  );

  const managementTokensCount = computed(() =>
    Array.isArray(managementTokens.value) ? managementTokens.value.length : 0
  );

  const canCreateAnotherSharedWheel = computed(() =>
    isUserLoggedIn.value ? true : managementTokensCount.value < 3
  );

  // Setters (Mutations)
  function setUserPhotoUrl(url) {
    userPhotoUrl.value = url || "/images/user_profile.png";
  }
  function setUserDisplayName(name) {
    displayName.value = name;
  }
  function setUserProfile(profile) {
    userProfile.value = profile;
  }
  function setIsExistingUser(val) {
    isExistingUser.value = val;
  }
  function setSavedWheels(wheels) {
    savedWheels.value = wheels;
  }
  function setSharedWheels(wheels) {
    sharedWheels.value = wheels;
  }
  function setManagementTokens(tokens) {
    managementTokens.value = Array.isArray(tokens) ? tokens : [];
  }
  function setAuthenticated(val) {
    isAuthenticated.value = !!val;
  }
  function clearUser() {
    userPhotoUrl.value = "";
    displayName.value = "";
    savedWheels.value = [];
    sharedWheels.value = [];
    userProfile.value = null;
    isExistingUser.value = false;
    managementTokens.value = [];
  }
  function setInitialized(val) {
    initialized.value = val;
  }

  // Actions
  async function postSignIn() {
    try {
      // Handle account conversion if an anonymous token was saved before login
      try {
        const anonToken = sessionStorage.getItem("anonTokenBeforeLogin");
        if (anonToken) {
          const response = await CloudFunctions.convertAccount(
            anonToken,
            await AuthService.getUserIdToken()
          );
          if (!response.success) {
            console.warn("Account conversion warning:", response.error);
          }
          sessionStorage.removeItem("anonTokenBeforeLogin");
        }
      } catch (e) {
        console.warn("Account conversion failed:", e);
      }

      // 1. Update Local User State (Immediate feedback)
      const current = AuthService.getCurrentUser();
      if (current) {
        setUserPhotoUrl(current.photoURL);
        setUserDisplayName(current.displayName || current.email || "");
      }

      const adminStore = useAdminStore();

      // 2. Execute Parallel Remote Tasks
      // We use Promise.all to run these concurrently instead of sequentially
      await Promise.all([
        // Task A: Upsert Profile & Load Profile Data
        (async () => {
          try {
            await CloudFunctions.upsertUserProfile();
            if (current) {
              await loadUserProfile(current);
            }
          } catch (e) {
            console.warn("Profile sync warning:", e);
          }
        })(),

        // Task B: Sync Tokens -> Then Load Shared Wheels
        // We chain these because shared wheels depend on the synced tokens
        (async () => {
          await syncTokensFromFirestore();
          return loadSharedWheels();
        })(),

        // Task C: Load Saved Wheels
        loadSavedWheels(),

        // Task D: Check Admin Status
        adminStore.checkAdminStatus(true),
      ]);

      return true;
    } catch (e) {
      console.error("postSignIn failed:", e);
      return false;
    }
  }

  async function handleAuthStateChange(user) {
    const adminStore = useAdminStore();

    if (user && !user.isAnonymous) {
      setAuthenticated(true);
      setUserPhotoUrl(user.photoURL);
      setUserDisplayName(user.displayName || user.email || "");
      await postSignIn();
    } else {
      setAuthenticated(false);
      clearUser();
      // Clear admin status when user logs out
      adminStore.clearAdminStatus();
    }
  }

  async function initialize() {
    if (initialized.value) return true;

    try {
      await initializeFirebase();

      const initialUser = await AuthService.waitForAuthReady();

      AuthService.addAuthStateListener(async (user) => {
        try {
          await handleAuthStateChange(user);
        } catch (error) {
          console.error("Error in auth state listener:", error);
          clearUser();
        }
      });

      await handleAuthStateChange(initialUser);

      setInitialized(true);
      return true;
    } catch (error) {
      console.error("Error initializing Firebase services:", error);
      setInitialized(true);
      return false;
    }
  }

  async function userIsLoggedInAction() {
    try {
      if (!initialized.value) {
        await initialize();
      }

      const loggedIn = AuthService.isUserLoggedIn();

      if (loggedIn) {
        const user = AuthService.getCurrentUser();

        setUserPhotoUrl(user.photoURL);
        setUserDisplayName(user.displayName || user.email || "");

        await loadUserProfile(user);
      }
      return loggedIn;
    } catch (error) {
      console.error("Error in userIsLoggedIn:", error);
      return false;
    }
  }

  async function loadUserProfile(user) {
    try {
      const profile = await CloudFunctions.getUserProfile();

      const resolvedProfile =
        profile && typeof profile === "object"
          ? profile
          : {
              uid: user.uid,
              displayName: user.displayName || "",
              email: user.email || "",
              photoURL: user.photoURL || "",
              emailVerified: user.emailVerified || false,
              isAnonymous: user.isAnonymous || false,
              exists: false,
              providers: [],
            };

      setUserProfile(resolvedProfile);

      const exists = resolvedProfile.exists === true;
      const providers = Array.isArray(resolvedProfile.providers)
        ? resolvedProfile.providers
        : [];
      const hasEmail =
        !!resolvedProfile.email && !!resolvedProfile.email.trim();

      const existsUser = exists || providers.length > 0 || hasEmail;
      setIsExistingUser(!!existsUser);

      setUserDisplayName(
        resolvedProfile.displayName || user.displayName || user.email || ""
      );
      setUserPhotoUrl(resolvedProfile.photoURL || user.photoURL);

      return true;
    } catch (error) {
      console.error("Error loading user profile:", error);
      setUserProfile(null);
      setIsExistingUser(false);
      return false;
    }
  }

  async function logOut() {
    const adminStore = useAdminStore();
    try {
      await purgeAllManagementTokens();
    } catch (e) {
      console.warn("Failed to purge management tokens on logout:", e);
    }
    await AuthService.logOut();
    setAuthenticated(false);
    clearUser();
    adminStore.clearAdminStatus();
  }

  async function purgeAllManagementTokens() {
    try {
      const tokens = getManagementTokensFromStorage();
      if (Array.isArray(tokens) && tokens.length > 0) {
        try {
          localStorage.setItem("wheelManagementTokens", JSON.stringify([]));
        } catch {}
      }
    } catch (e) {
      console.warn("Error during purgeAllManagementTokens:", e);
    } finally {
      setManagementTokens([]);
      setSharedWheels([]);
    }
  }

  async function deleteAccount() {
    try {
      const response = await CloudFunctions.deleteAccount();
      if (!response.success) {
        throw response.error;
      }

      await AuthService.logOut();
      clearUser();

      return { success: true };
    } catch (error) {
      console.error("Account deletion failed:", error);
      throw error;
    }
  }

  async function loginAnonymously() {
    if (!initialized.value) {
      await initialize();
    }
    await AuthService.logInAnonymously();
  }

  async function logUserActivity() {
    if (!initialized.value) {
      await initialize();
    }
    await CloudFunctions.logUserActivity();
  }

  async function getUid() {
    return await AuthService.getUid();
  }

  async function loadSavedWheels() {
    const uid = await AuthService.getUid();
    if (uid) {
      const response = await CloudFunctions.getUserWheels();
      if (response.success) {
        setSavedWheels(response.data);
      } else {
        console.error("Error loading saved wheels:", response.error);
        setSavedWheels([]);
      }
      logUserActivity();
    }
  }

  async function logWheelRead(wheelTitle) {
    await CloudFunctions.logWheelRead(wheelTitle);
  }

  async function deleteSavedWheel(wheelTitle) {
    const uid = await AuthService.getUid();
    if (uid && wheelTitle) {
      const response = await CloudFunctions.deleteUserWheel(wheelTitle);
      if (!response.success) {
        console.error("Error deleting saved wheel:", response.error);
      }
      await loadSavedWheels();
    }
  }

  async function saveWheel(wheelConfig) {
    const uid = await AuthService.getUid();
    if (uid && wheelConfig) {
      const response = await CloudFunctions.saveUserWheel(wheelConfig);
      if (!response.success) {
        console.error("Error saving wheel:", response.error);
      }
      await loadSavedWheels();
    }
  }

  async function shareWheel({ wheelConfig, copyableWheel }) {
    const response = await CloudFunctions.createSharedWheel(
      copyableWheel,
      wheelConfig
    );

    if (!response.success) {
      console.error("ShareWheel failed:", response.error);
      const errorMessage =
        response.error?.message ||
        response.error ||
        "Failed to create shared wheel";
      throw new Error(errorMessage);
    }

    const result = response.data;

    if (result.managementToken) {
      await addManagementToken(result.managementToken);
    }

    return result.path;
  }

  async function saveTokenToFirestore(token) {
    try {
      const uid = await AuthService.getUid();
      if (!uid) return;

      const response = await CloudFunctions.addUserToken(token);
      if (!response.success) {
        console.error("Error saving token to Firestore:", response.error);
      }
    } catch (error) {
      console.error("Error saving token to Firestore:", error);
    }
  }

  async function removeTokenFromFirestore(token) {
    try {
      const uid = await AuthService.getUid();
      if (!uid) return;

      const response = await CloudFunctions.removeUserToken(token);
      if (!response.success) {
        console.error("Error removing token from Firestore:", response.error);
      }
    } catch (error) {
      console.error("Error removing token from Firestore:", error);
    }
  }

  async function loadSharedWheels() {
    try {
      const tokens = getManagementTokensFromStorage();

      if (tokens.length === 0) {
        setSharedWheels([]);
        return;
      }

      const response = await CloudFunctions.getSharedWheelsByTokens(tokens);
      if (response && response.success) {
        const wheels = Array.isArray(response.data) ? response.data : [];
        setSharedWheels(wheels);

        try {
          const localTokens = getManagementTokensFromStorage();
          const returned = new Set(
            wheels.map((w) => w && w.managementToken).filter(Boolean)
          );
          const invalid = localTokens.filter((t) => !returned.has(t));
          if (invalid.length > 0) {
            const validTokens = localTokens.filter((t) => returned.has(t));
            localStorage.setItem(
              "wheelManagementTokens",
              JSON.stringify(validTokens)
            );
            setManagementTokens(validTokens);
            if (AuthService.isUserLoggedIn()) {
              for (const token of invalid) {
                try {
                  await removeTokenFromFirestore(token);
                } catch (e) {
                  console.error(
                    "Error removing invalid token from Firestore:",
                    token,
                    e
                  );
                }
              }
            }
          }
        } catch (e) {
          console.error("Error pruning invalid management tokens:", e);
        }

        logUserActivity();
      } else {
        console.error(
          "Error loading shared wheels by tokens:",
          response ? response.error : "No response"
        );
        setSharedWheels([]);
      }
    } catch (error) {
      console.error("Unexpected error in loadSharedWheels:", error);
      setSharedWheels([]);
    }
  }

  async function addManagementToken(token) {
    if (!token || !token.startsWith("mgmt-")) return;

    const tokens = getManagementTokensFromStorage();
    if (!tokens.includes(token)) {
      tokens.push(token);
      localStorage.setItem("wheelManagementTokens", JSON.stringify(tokens));
    }

    if (AuthService.isUserLoggedIn()) {
      await saveTokenToFirestore(token);
    }

    setManagementTokens(getManagementTokensFromStorage());
    await loadSharedWheels();
  }

  async function removeManagementToken(token) {
    if (!token) return;

    const tokens = getManagementTokensFromStorage();
    const filteredTokens = tokens.filter((t) => t !== token);
    localStorage.setItem(
      "wheelManagementTokens",
      JSON.stringify(filteredTokens)
    );

    if (AuthService.isUserLoggedIn()) {
      await removeTokenFromFirestore(token);
    }

    setManagementTokens(getManagementTokensFromStorage());
    await loadSharedWheels();
  }

  async function syncTokensFromFirestore() {
    try {
      const uid = await AuthService.getUid();
      if (!uid) return;

      const response = await CloudFunctions.getUserTokens();
      const firestoreTokens = response.success ? response.data : [];
      const localTokens = getManagementTokensFromStorage();

      const allTokens = [...new Set([...localTokens, ...firestoreTokens])];

      localStorage.setItem("wheelManagementTokens", JSON.stringify(allTokens));
      setManagementTokens(allTokens);

      const tokensToSync = allTokens.filter(
        (token) => !firestoreTokens.includes(token)
      );

      if (tokensToSync.length > 0) {
        await Promise.all(
          tokensToSync.map((token) => saveTokenToFirestore(token))
        );
      }
    } catch (error) {
      console.error("Error syncing tokens from Firestore:", error);
    }
  }

  async function pruneInvalidTokens() {
    try {
      const tokens = getManagementTokensFromStorage();
      if (!Array.isArray(tokens) || tokens.length === 0) return 0;

      const response = await CloudFunctions.getSharedWheelsByTokens(tokens);
      if (!response.success) return tokens.length;

      const wheels = Array.isArray(response.data) ? response.data : [];
      const returned = new Set(
        wheels.map((w) => w && w.managementToken).filter(Boolean)
      );
      const invalid = tokens.filter((t) => !returned.has(t));

      if (invalid.length > 0) {
        const validTokens = tokens.filter((t) => returned.has(t));
        localStorage.setItem(
          "wheelManagementTokens",
          JSON.stringify(validTokens)
        );
        setManagementTokens(validTokens);

        if (AuthService.isUserLoggedIn()) {
          for (const token of invalid) {
            try {
              await removeTokenFromFirestore(token);
            } catch (e) {
              console.error(
                "Error removing invalid token from Firestore:",
                token,
                e
              );
            }
          }
        }

        await loadSharedWheels();
        return validTokens.length;
      }

      return tokens.length;
    } catch (e) {
      console.error("Error pruning invalid management tokens:", e);
      return 0;
    }
  }

  async function ensurePolicyFresh() {
    await initialize();

    try {
      if (typeof AuthService.waitForAuthReady === "function") {
        await AuthService.waitForAuthReady();
      }
    } catch (e) {
      console.warn("waitForAuthReady failed:", e);
    }

    if (AuthService.isUserLoggedIn()) {
      await syncTokensFromFirestore();
      try {
        await loadSavedWheels();
      } catch (e) {
        console.warn("Failed to load saved wheels during policy refresh:", e);
      }
    } else {
      await pruneInvalidTokens();
    }

    const tokensNow = getManagementTokensFromStorage();
    setManagementTokens(tokensNow);

    return {
      isLoggedIn: AuthService.isUserLoggedIn(),
      managementTokensCount: Array.isArray(tokensNow) ? tokensNow.length : 0,
      canCreateAnotherSharedWheel:
        AuthService.isUserLoggedIn() ||
        (Array.isArray(tokensNow) ? tokensNow.length : 0) < 3,
    };
  }

  async function deleteSharedWheelByToken(token) {
    if (!token || !token.startsWith("mgmt-")) {
      throw new Error("Invalid management token");
    }

    const response = await CloudFunctions.deleteSharedWheelByToken(token);

    if (response.success) {
      await removeManagementToken(token);
    } else {
      throw response.error;
    }
  }

  async function deleteSharedWheel(path) {
    const wheel = sharedWheels.value.find((w) => w.path === path);
    if (!wheel || !wheel.managementToken) {
      throw new Error("Unable to find management token for wheel");
    }

    await deleteSharedWheelByToken(wheel.managementToken);
  }

  async function sendVerificationEmail() {
    if (!initialized.value) {
      await initialize();
    }
    if (!AuthService.isUserLoggedIn()) {
      throw new Error("Must be signed in to send verification email");
    }
    await AuthService.sendVerificationEmailToCurrentUser();
    return true;
  }

  return {
    // State
    userPhotoUrl,
    displayName,
    savedWheels,
    sharedWheels,
    managementTokens,
    initialized,
    isAuthenticated,
    userProfile,
    isExistingUser,

    // Getters
    isInitialized,
    getUserPhotoUrl,
    getDisplayName,
    userEmail,
    isAuthReady,
    isUserLoggedIn,
    hasGoogleLinked,
    currentUser,
    isEmailVerified,
    providers,
    hasPasswordProvider,
    managementTokensCount,
    canCreateAnotherSharedWheel,

    // Actions
    setUserPhotoUrl,
    setUserDisplayName,
    setUserProfile,
    setIsExistingUser,
    setSavedWheels,
    setSharedWheels,
    setManagementTokens,
    setAuthenticated,
    clearUser,
    setInitialized,
    postSignIn,
    initialize,
    handleAuthStateChange,
    userIsLoggedInAction,
    loadUserProfile,
    logOut,
    purgeAllManagementTokens,
    deleteAccount,
    loginAnonymously,
    logUserActivity,
    getUid,
    loadSavedWheels,
    loadSharedWheels,
    logWheelRead,
    deleteSavedWheel,
    saveWheel,
    shareWheel,
    addManagementToken,
    removeManagementToken,
    syncTokensFromFirestore,
    pruneInvalidTokens,
    ensurePolicyFresh,
    deleteSharedWheelByToken,
    deleteSharedWheel,
    sendVerificationEmail,
  };
});
